/**
 * InsightPro™ — Executive Dashboard  v6.0
 * Data sourced via pbiQuery → Power BI Pro per-client dataset.
 * All DAX queries use raw SUM('Transactions'[SALES]) with TXDATE filters
 * for true period-vs-prior-period comparisons.
 */
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}
import {
  KPICard, SectionHeader, Card, CardTitle,
  Flag, RevenueTrendChart, MetricRow, Skeleton,
} from '../components/UI';
import { pbiQuery } from '../utils/api';
import { getFirestore, collection, query as fsQuery, where, orderBy, limit, getDocs, updateDoc, doc } from 'firebase/firestore';

// ── Period → date range ───────────────────────────────────────
function getDateRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  const dow = today.getDay();

  let startDate, endDate;

  if (period === 'TODAY') {
    startDate = `${y},${m},${d}`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'YESTERDAY' || period === 'YEST') {
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const yy = yest.getFullYear(), ym = yest.getMonth() + 1, yd = yest.getDate();
    startDate = `${yy},${ym},${yd}`;
    endDate   = `${yy},${ym},${yd}`;
  } else if (period === 'WTD') {
    const start = new Date(today);
    start.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    startDate = `${start.getFullYear()},${start.getMonth()+1},${start.getDate()}`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'MTD') {
    startDate = `${y},${m},1`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'QTD') {
    const qStart = Math.floor((m - 1) / 3) * 3 + 1;
    startDate = `${y},${qStart},1`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'YTD') {
    startDate = `${y},1,1`;
    endDate   = `${y},${m},${d}`;
  } else {
    startDate = `${y},${m},1`;
    endDate   = `${y},${m},${d}`;
  }

  const [sy, sm, sd] = startDate.split(',').map(Number);
  const [ey, em, ed] = endDate.split(',').map(Number);
  return {
    startDate, endDate,
    pyStart: `${sy-1},${sm},${sd}`,
    pyEnd:   `${ey-1},${em},${ed}`,
  };
}

const fmt  = (n) => n == null ? '—' : `R ${Number(n).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
const fmtM = (n) => {
  if (n == null) return '—';
  if (n >= 1000000) return `R ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `R ${(n / 1000).toFixed(0)}K`;
  return fmt(n);
};

export default function Dashboard() {
  const { period }  = useOutletContext();
  const { selectedCompany } = useCompany();
  const isMobile    = useIsMobile();
  const [kpis,      setKpis]      = useState(null);
  const [trend,     setTrend]     = useState([]);
  const [branches,  setBranches]  = useState([]);
  const [stock,     setStock]     = useState(null);
  const [debtors,   setDebtors]   = useState(null);
  const [creditors, setCreditors] = useState(null);
  const [alerts,    setAlerts]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [errors,    setErrors]    = useState({});

  useEffect(() => {
    setLoading(true);
    setErrors({});

    const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
    const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
    const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;

    const kpiDax = `EVALUATE ROW(
      "RevenueMTD",       CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
      "RevenueLastYear",  CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
      "GrossPurchases",   CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF}),
      "UnitsSold",        CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
      "TotalStockValue",  SUMX(FILTER('StockBalances','StockBalances'[ONHAND]>0), 'StockBalances'[ONHAND] * RELATED('Stock'[AVRGCOST])),
      "YTDPurchases",     CALCULATE(SUM('Transactions'[PURCHASES]), 'Transactions'[TXDATE] >= DATE(${`${new Date().getFullYear()},1,1`}) && 'Transactions'[TXDATE] <= DATE(${`${new Date().getFullYear()},${new Date().getMonth()+1},${new Date().getDate()}`})),
      "YTDDays",          DATEDIFF(DATE(${`${new Date().getFullYear()},1,1`}), TODAY(), DAY)
    )`;

    const trendDax = `EVALUATE
      ADDCOLUMNS(
        FILTER(
          SUMMARIZE('Transactions', 'Date'[YYYYMM], 'Date'[MonthName], 'Date'[Year], 'Date'[MonthNo]),
          'Date'[YYYYMM] >= 202404
        ),
        "Revenue",   CALCULATE(SUM('Transactions'[SALES])),
        "Purchases", CALCULATE(SUM('Transactions'[PURCHASES]))
      )
      ORDER BY 'Date'[YYYYMM] ASC`;

    const branchDax = `EVALUATE
      TOPN(6,
        ADDCOLUMNS(
          SUMMARIZE(
            FILTER('Transactions', ${cyF}),
            'CompanyList'[COMPANY NAME]
          ),
          "Revenue",  CALCULATE(SUM('Transactions'[SALES]), ${cyF}),
          "LastYear", CALCULATE(SUM('Transactions'[SALES]), ${pyF}),
          "Growth",   DIVIDE(
            CALCULATE(SUM('Transactions'[SALES]), ${cyF}) - CALCULATE(SUM('Transactions'[SALES]), ${pyF}),
            CALCULATE(SUM('Transactions'[SALES]), ${pyF})
          )
        ),
        [Revenue], DESC
      )
      ORDER BY [Revenue] DESC`;

    const stockDax = `EVALUATE ROW(
      "TotalStockValue", SUMX(FILTER('StockBalances','StockBalances'[ONHAND]>0), 'StockBalances'[ONHAND] * RELATED('Stock'[AVRGCOST])),
      "TotalLines",      COUNTROWS(FILTER('Stock', 'Stock'[ONHAND] > 0))
    )`;

    const creditorsDax = `EVALUATE ROW(
      "TotalBook",     CALCULATE(SUM('Creditors'[TOTAL]),   'Creditors'[TOTAL] > 0),
      "Overdue60Plus", CALCULATE(SUM('Creditors'[DAYS_60]) + SUM('Creditors'[DAYS_90]) + SUM('Creditors'[DAYS_120]) + SUM('Creditors'[DAYS_150]) + SUM('Creditors'[DAYS_180]), 'Creditors'[TOTAL] > 0),
      "Days180Plus",   CALCULATE(SUM('Creditors'[DAYS_180]),'Creditors'[TOTAL] > 0),
      "ActiveCreditors", CALCULATE(COUNTROWS('Creditors'), 'Creditors'[TOTAL] > 0)
    )`;

    const debtorsDax = `EVALUATE ROW(
      "TotalBook",     CALCULATE(SUM('Debtors'[TOTAL]),   'Debtors'[TOTAL] > 0),
      "Overdue60Plus", CALCULATE(SUM('Debtors'[DAYS_60]) + SUM('Debtors'[DAYS_90]) + SUM('Debtors'[DAYS_120]) + SUM('Debtors'[DAYS_150]) + SUM('Debtors'[DAYS_180]), 'Debtors'[TOTAL] > 0),
      "Days180Plus",   CALCULATE(SUM('Debtors'[DAYS_180]),'Debtors'[TOTAL] > 0),
      "ActiveDebtors", CALCULATE(COUNTROWS('Debtors'), 'Debtors'[TOTAL] > 0)
    )`;

    const handle = (key, dax) =>
      pbiQuery({ dax })
        .then(r => r?.rows || [])
        .catch(err => {
          console.error(`[Dashboard] ${key} failed:`, err.message);
          setErrors(prev => ({ ...prev, [key]: err.message }));
          return [];
        });

    Promise.all([
      handle('kpis',     kpiDax),
      handle('trend',    trendDax),
      handle('branches', branchDax),
      handle('stock',    stockDax),
      handle('debtors',  debtorsDax),
      pbiQuery({ dax: creditorsDax }).then(r => r?.rows || []).catch(() => []), // silent — Creditors table may not exist
    ]).then(([k, t, b, s, d, c]) => {
      const kpiRow = Array.isArray(k) ? k[0] : k;
      const rev       = kpiRow?.['[RevenueMTD]'];
      const lastYr    = kpiRow?.['[RevenueLastYear]'];
      const purch     = kpiRow?.['[GrossPurchases]'];
      const gp        = rev && purch ? (rev - purch) / rev * 100 : null;
      const growthPct = rev && lastYr ? ((rev - lastYr) / lastYr * 100).toFixed(1) : null;

      // Stock Days on Hand = Stock Value at Cost ÷ (Annual Purchases ÷ 365)
      const stockVal   = kpiRow?.['[TotalStockValue]'];
      const ytdPurch   = kpiRow?.['[YTDPurchases]'];
      const ytdDays    = kpiRow?.['[YTDDays]'] || 1;
      const annualPurch = ytdPurch && ytdDays > 0 ? (ytdPurch / ytdDays) * 365 : null;
      const daysOnHand  = stockVal && annualPurch ? (stockVal / (annualPurch / 365)) : null;

      setKpis({
        RevenueMTD:       rev,
        RevenueGrowthPct: growthPct,
        GrossMarginPct:   gp != null ? gp.toFixed(1) : null,
        MarginVsTarget:   null,
        UnitsSold:        kpiRow?.['[UnitsSold]'],
        AvgDaysOnHand:    daysOnHand != null ? Math.round(daysOnHand) : null,
        Overdue60Plus:    null,
        DebtorsPct:       null,
        RevenueSpark:     [],
        MarginSpark:      [],
      });

      setTrend(t
        .filter(r => r['[Revenue]'] != null)
        .slice(-12)
        .map(r => ({
          Period:      `${r['Date[MonthName]']?.slice(0,3)} ${String(r['Date[Year]']).slice(2)}`,
          revenue:     r['[Revenue]'],
          grossProfit: r['[Revenue]'] - (r['[Purchases]'] || 0),
          YYYYMM:      r['Date[YYYYMM]'],
        }))
      );

      setBranches(b.map(r => ({
        StoreName:       r['CompanyList[COMPANY NAME]'],
        Revenue:         r['[Revenue]'],
        RevenueSharePct: null,
        GrowthPct:       r['[Growth]'] != null ? (r['[Growth]'] * 100).toFixed(1) : null,
        MoMPct:          r['[Growth]'] != null ? (r['[Growth]'] * 100).toFixed(1) : null,
      })));

      const stockRow = Array.isArray(s) ? s[0] : s;
      setStock({
        TotalStockValue: stockRow?.['[TotalStockValue]'],
        DaysOnHand:      null,
        TotalLines:      stockRow?.['[TotalLines]'],
      });

      const debtorRow  = Array.isArray(d) ? d[0] : d;
      const totalBook  = debtorRow?.['[TotalBook]']     || 0;
      const overdue60  = debtorRow?.['[Overdue60Plus]'] || 0;
      const days180    = debtorRow?.['[Days180Plus]']   || 0;
      const activeAcc  = debtorRow?.['[ActiveDebtors]'] || 0;
      // Parse creditors
      const credData = Array.isArray(c) ? c[0] : c;
      if (credData) {
        const credBook    = credData?.['[TotalBook]']       || 0;
        const credOverdue = credData?.['[Overdue60Plus]']   || 0;
        setCreditors({
          TotalBook:     credBook,
          Overdue60Plus: credOverdue,
          Days180Plus:   credData?.['[Days180Plus]']     || 0,
          ActiveCreditors: credData?.['[ActiveCreditors]'] || 0,
          OverduePct:    credBook > 0 ? ((credOverdue / credBook) * 100).toFixed(1) : null,
        });
      }

      setDebtors({
        TotalBook:     totalBook,
        Overdue60Plus: overdue60,
        Days180Plus:   days180,
        ActiveDebtors: activeAcc,
        OverduePct:    totalBook > 0 ? ((overdue60 / totalBook) * 100).toFixed(1) : null,
      });

      setLoading(false);
    });
  }, [period]);

  // Fetch active alerts from Firestore
  useEffect(() => {
    (async () => {
      try {
        const db2 = getFirestore();
        const companyId = selectedCompany?.id || selectedCompany?.companyId || null;
        if (!companyId) { console.warn('Alerts: no companyId in context'); return; }
        const snap = await getDocs(
          fsQuery(
            collection(db2, 'companies', companyId, 'alerts'),
            where('status', '==', 'active'),
            orderBy('detectedAt', 'desc'),
            limit(10)
          )
        );
        console.log('Alerts: found', snap.size, 'alerts');
        setAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.warn('Alerts fetch error:', e.message); }
    })();
  }, [selectedCompany]);

  const resolveAlert = async (alertId) => {
    try {
      const db2 = getFirestore();
      const companyId = selectedCompany?.id || selectedCompany?.companyId || null;
      if (!companyId) return;
      await updateDoc(doc(db2, 'companies', companyId, 'alerts', alertId), {
        status: 'resolved',
        resolvedAt: new Date(),
        resolvedBy: 'USER',
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (e) { console.warn('Resolve alert:', e.message); }
  };

  const sparkData = (arr) => (arr || []).map(v => ({ v }));

  return (
    <div style={{ animation: 'fadeUp 0.5s ease both', minWidth: 0, overflowX: 'hidden' }}>

      {/* KPI STRIP */}
      <SectionHeader title="Key Performance Indicators" />
      {isMobile ? (
        /* Mobile: 2-column grid — same pill style as desktop */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24, minWidth: 0, overflowX: 'hidden' }}>
          {[
            { label: 'Total Revenue', value: loading ? '—' : fmtM(kpis?.RevenueMTD), delta: loading ? null : `${Number(kpis?.RevenueGrowthPct) > 0 ? '+' : ''}${kpis?.RevenueGrowthPct ?? '—'}% vs prior`, deltaType: Number(kpis?.RevenueGrowthPct) > 0 ? 'up' : 'down' },
            { label: 'Gross Margin',  value: loading ? '—' : `${kpis?.GrossMarginPct ?? '—'}%`,          delta: 'excl. overhead', deltaType: 'neutral' },
            { label: 'Units Sold',    value: loading ? '—' : String(kpis?.UnitsSold != null ? Number(kpis.UnitsSold).toLocaleString('en-ZA') : '—'), delta: 'current period', deltaType: 'neutral' },
            { label: 'Top Branch',    value: loading ? '—' : (branches[0]?.StoreName || '—')?.split(' ').slice(-2).join(' '), delta: loading ? null : fmtM(branches[0]?.Revenue), deltaType: 'up' },
          ].map((k, i) => (
            <div key={i} style={{
              background: 'var(--card)',
              border: '1px solid var(--card-border)',
              borderRadius: 4, padding: '14px 14px 12px',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8, lineHeight: 1.3 }}>{k.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1, marginBottom: 6, wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.value}</div>
              {k.delta && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: k.deltaType === 'up' ? 'var(--green)' : k.deltaType === 'down' ? 'var(--red)' : 'var(--page-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {k.deltaType === 'up' ? '↑ ' : k.deltaType === 'down' ? '↓ ' : ''}{k.delta}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Desktop: 5-column grid — Sales page style */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 10, marginBottom: 28 }}>
          {[
            { label: 'Total Revenue',       value: loading ? '—' : fmtM(kpis?.RevenueMTD),              sub: loading ? null : `${Number(kpis?.RevenueGrowthPct) > 0 ? '+' : ''}${kpis?.RevenueGrowthPct ?? '—'}% vs prior period`, subColor: Number(kpis?.RevenueGrowthPct) > 0 ? 'var(--green)' : 'var(--red)' },
            { label: 'Gross Margin %',      value: loading ? '—' : `${kpis?.GrossMarginPct ?? '—'}%`,   sub: 'excl. overhead',      subColor: 'var(--text-muted)' },
            { label: 'Stock Days on Hand',  value: loading ? '—' : `${kpis?.AvgDaysOnHand ?? '—'}d`,    sub: loading ? null : (kpis?.AvgDaysOnHand > 35 ? `+${(kpis.AvgDaysOnHand-35).toFixed(1)}d above target` : 'Within target'), subColor: kpis?.AvgDaysOnHand > 35 ? 'var(--red)' : 'var(--green)' },
            { label: 'Debtors > 60 Days',   value: loading ? '—' : fmtM(debtors?.Overdue60Plus), sub: loading ? null : (debtors?.OverduePct != null ? `${debtors.OverduePct}% of book` : 'No data'), subColor: Number(debtors?.OverduePct) > 30 ? 'var(--red)' : Number(debtors?.OverduePct) > 15 ? 'var(--amber)' : 'var(--green)' },
            { label: 'Top Branch',          value: loading ? '—' : (branches[0]?.StoreName || '—'),     sub: loading ? null : `${fmtM(branches[0]?.Revenue)} · ${Number(branches[0]?.GrowthPct) > 0 ? '+' : ''}${branches[0]?.GrowthPct ?? '—'}%`, subColor: 'var(--text-muted)' },
          ].map((k, i) => (
            <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 18px' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 }}>{k.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1, marginBottom: 6 }}>{k.value}</div>
              {k.sub && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: k.subColor || 'var(--page-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* MID ROW */}
      <SectionHeader title="Revenue Trend & Branch Ranking" />
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 320px',
        gap: 16, marginBottom: 28,
        minWidth: 0, overflowX: 'hidden',
      }}>
        <Card delay={0.3}>
          <CardTitle>Revenue vs Gross Profit — 6 Month Trend</CardTitle>
          {loading
            ? <Skeleton height={180} />
            : errors.trend
              ? <ApiError message={errors.trend} />
              : <RevenueTrendChart data={trend} />
          }
        </Card>

        <Card delay={0.35}>
          <CardTitle>Branch Performance</CardTitle>
          {loading
            ? [1,2,3,4,5,6].map(i => <Skeleton key={i} height={38} style={{ marginBottom: 6 }} />)
            : errors.branches
              ? <ApiError message={errors.branches} />
              : (() => {
                  const maxRev = Math.max(...branches.map(b => b.Revenue || 0));
                  return branches.slice(0, 8).map((b, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10,
                      padding: isMobile ? '10px 12px' : '9px 20px',
                      borderBottom: i < Math.min(branches.length, 8) - 1 ? '1px solid var(--card-border)' : 'none',
                      minHeight: 44, minWidth: 0, overflow: 'hidden',
                    }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', width: 18, flexShrink: 0 }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ fontSize: 12, color: 'var(--page-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                          {b.StoreName}
                        </div>
                        <div style={{ height: 3, background: 'var(--card-border)', borderRadius: 2, marginTop: 4 }}>
                          <div style={{ height: 3, width: `${maxRev > 0 ? Math.min(100, (b.Revenue / maxRev) * 100) : 0}%`, background: 'var(--gold)', borderRadius: 2 }} />
                        </div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--page-text)', width: 60, textAlign: 'right', flexShrink: 0 }}>
                        {fmtM(b.Revenue)}
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: Number(b.GrowthPct) >= 0 ? 'var(--green)' : 'var(--red)', width: 42, textAlign: 'right', flexShrink: 0 }}>
                        {b.GrowthPct != null ? `${Number(b.GrowthPct) > 0 ? '+' : ''}${b.GrowthPct}%` : '—'}
                      </div>
                    </div>
                  ));
                })()
          }
        </Card>
      </div>

      {/* BOTTOM ROW */}
      <SectionHeader title={`Stock · Debtors${creditors ? " · Creditors" : ""} · Exceptions`} />
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : creditors ? '1fr 1fr 1fr 300px' : '1fr 1fr 300px',
        gap: 16, marginBottom: 24,
        minWidth: 0, overflowX: 'hidden',
      }}>

        {/* Stock */}
        <Card delay={0.4}>
          <CardTitle>
            Stock Intelligence
            {stock && <Flag type="ok">OK</Flag>}
          </CardTitle>
          {loading
            ? [1,2,3,4,5].map(i => <Skeleton key={i} height={28} style={{ marginBottom: 4 }} />)
            : errors.stock
              ? <ApiError message={errors.stock} />
              : <>
                <MetricRow label="Total Stock Value"  value={fmtM(stock?.TotalStockValue)} />
                <MetricRow label="Days on Hand"       value={kpis?.AvgDaysOnHand != null ? `${kpis.AvgDaysOnHand}d` : '—'} />
                <MetricRow label="Active Lines"       value={stock?.TotalLines ?? '—'} last />
              </>
          }
        </Card>

        {/* Debtors */}
        <Card delay={0.45}>
          <CardTitle>Debtors Ageing</CardTitle>
          {loading
            ? [1,2,3,4,5].map(i => <Skeleton key={i} height={28} style={{ marginBottom: 4 }} />)
            : debtors
              ? <>
                  <MetricRow label="Total Book"      value={fmtM(debtors.TotalBook)} />
                  <MetricRow label="Overdue 60+ Days" value={fmtM(debtors.Overdue60Plus)} />
                  <MetricRow label="180+ Days Risk"  value={fmtM(debtors.Days180Plus)} />
                  <MetricRow label="Active Accounts" value={debtors.ActiveDebtors} last />
                </>
              : <div style={{ color:'var(--page-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>No data</div>
          }
        </Card>

        {/* Creditors — only show if data exists */}
        {(loading || creditors) && (
          <Card delay={0.47}>
            <CardTitle>Creditors Ageing</CardTitle>
            {loading
              ? [1,2,3,4].map(i => <Skeleton key={i} height={28} style={{ marginBottom: 4 }} />)
              : creditors
                ? <>
                    <MetricRow label="Total Book"       value={fmtM(creditors.TotalBook)} />
                    <MetricRow label="Overdue 60+ Days" value={fmtM(creditors.Overdue60Plus)} valueColor={Number(creditors.OverduePct) > 30 ? 'var(--red)' : Number(creditors.OverduePct) > 15 ? 'var(--amber)' : 'var(--green)'} />
                    <MetricRow label="180+ Days"        value={fmtM(creditors.Days180Plus)} valueColor={creditors.Days180Plus > 0 ? 'var(--red)' : 'var(--green)'} />
                    <MetricRow label="Active Suppliers" value={creditors.ActiveCreditors} last />
                  </>
                : null
            }
          </Card>
        )}

        {/* Alerts & Exceptions */}
        <Card delay={0.5}>
          <CardTitle>Alerts & Exceptions</CardTitle>
          {loading
            ? [1,2,3].map(i => <Skeleton key={i} height={52} style={{ marginBottom: 8 }} />)
            : alerts.length === 0
              ? <div style={{ color:'var(--green)', fontFamily:'var(--font-mono)', fontSize:11 }}>
                  No active exceptions
                </div>
              : <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {alerts.map(alert => (
                    <ExceptionItem
                      key={alert.id}
                      alert={alert}
                      onResolve={() => resolveAlert(alert.id)}
                    />
                  ))}
                </div>
          }
        </Card>
      </div>

      <AdvisoryStrip />
    </div>
  );
}

// FIX issue 10: ExceptionItem now renders a resolve button and calls onResolve
function ExceptionItem({ alert, onResolve }) {
  const [resolving, setResolving] = useState(false);

  // Support both old (BigQuery) and new (Firestore) field names
  const alertType  = alert.type        || alert.ExceptionType || 'WARNING';
  const alertTitle = alert.title       || alert.Title         || 'Alert';
  const alertAt    = alert.detectedAt?.toDate?.() || (alert.DetectedAt ? new Date(alert.DetectedAt) : new Date());

  const typeColor =
    alertType === 'CRITICAL' ? 'var(--red)'   :
    alertType === 'WARNING'  ? 'var(--amber)' : 'var(--blue)';

  const ts = alertAt.toLocaleDateString('en-ZA', {
    day: 'numeric', month: 'short',
  });

  const handleResolve = async () => {
    setResolving(true);
    try { await onResolve(); }
    catch { setResolving(false); }
  };

  return (
    <div style={{
      padding: '9px 0',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 9,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: typeColor,
        }}>● {alertType}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-muted)' }}>{ts}</span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-sec)', lineHeight: 1.4, marginBottom: 6 }}>
        {alertTitle}
      </div>
      {/* FIX issue 10: resolve button is now actually rendered */}
      <button
        onClick={handleResolve}
        disabled={resolving}
        style={{
          fontFamily: 'var(--font-mono)', fontSize: 9,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: resolving ? 'var(--text-muted)' : 'var(--gold)',
          background: 'transparent',
          border: '1px solid var(--border)',
          padding: '3px 8px',
          cursor: resolving ? 'default' : 'pointer',
        }}
      >{resolving ? 'Resolving...' : 'Resolve'}</button>
    </div>
  );
}

// FIX issue 11: visible error component
function ApiError({ message }) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(212,95,95,0.07)',
      border: '1px solid rgba(212,95,95,0.2)',
      borderRadius: 2,
    }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--red)', marginBottom: 4 }}>
        DATA LOAD ERROR
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
        {message || 'Could not load data. Check function logs.'}
      </div>
    </div>
  );
}

function AdvisoryStrip() {
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(196,160,80,0.07), rgba(196,160,80,0.02))',
      border: '1px solid var(--border-gold)',
      padding: '16px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexWrap: 'wrap', gap: 12,
      animation: 'fadeUp 0.5s ease 0.55s both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 36, height: 36,
          border: '1px solid var(--border-gold)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--gold)',
        }}>§</div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--gold-light)' }}>
            Monthly Advisory Notes — Ready for Review
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
            Strategic analysis prepared for your business
          </div>
        </div>
      </div>
      <a href="/advisory" style={{
        fontFamily: 'var(--font-mono)', fontSize: 10,
        letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--gold)', padding: '7px 16px',
        border: '1px solid var(--border-gold)',
        textDecoration: 'none',
      }}>View Advisory →</a>
    </div>
  );
}
